import { useState, type CSSProperties } from "react";
import { EmptyState, showToast } from "../primitives";
import { Panel, PanelRow } from "./Panel";
import { useAgentTaskQueue, useAgentTaskPlan, useCompleteTask, useMoveTaskToTop } from "../../hooks/use-agent-tasks-26";
import { NewTaskModal } from "../scope/NewTaskModal";
import { TaskStatusPill } from "../tasks/TaskStatusPill";
import type { Task } from "../../../lib/types";

interface TasksPanelProps {
  nodeId: string;
  /** Walk child agents too (used at the workspace root). Default false. */
  includeSubtree?: boolean;
}

type Lens = "queue" | "plan";

/**
 * Sprint 27.0 — the unified Tasks panel (the old Priorities + Tasks tabs merged
 * into one). Two lenses on one task model:
 *   - Queue: the ranked list (sort_order). #1 = the agent's current task.
 *   - Plan:  the nested sub-task tree (parent_task_id), rendered recursively.
 * The 24.x cockpit Panel chrome (title + count + New task) is preserved.
 */
export function TasksPanel({ nodeId }: TasksPanelProps) {
  const [lens, setLens] = useState<Lens>("queue");
  const [newOpen, setNewOpen] = useState(false);

  const queueQuery = useAgentTaskQueue(nodeId, lens === "queue");
  const planQuery = useAgentTaskPlan(nodeId, {}, lens === "plan");

  const queue = queueQuery.data ?? [];
  const plan = planQuery.data ?? [];
  const active = lens === "queue" ? queueQuery : planQuery;
  const count = lens === "queue" ? queue.length : plan.length;

  const headerActions = (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={segmented} role="tablist" aria-label="Task lens">
        <button role="tab" aria-selected={lens === "queue"} style={segBtn(lens === "queue")} onClick={() => setLens("queue")}>Queue</button>
        <button role="tab" aria-selected={lens === "plan"} style={segBtn(lens === "plan")} onClick={() => setLens("plan")}>Plan</button>
      </div>
      <button onClick={() => setNewOpen(true)} style={headerBtnStyle} title="Create a task">
        <i className="ti ti-plus" style={{ fontSize: 13 }} aria-hidden="true" /> New task
      </button>
    </div>
  );

  return (
    <>
      <Panel
        title="Tasks"
        count={count}
        action={headerActions}
        isLoading={active.isLoading}
        error={active.error}
        onRetry={() => active.refetch()}
        isEmpty={count === 0}
        empty={
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <EmptyState>No tasks for this agent yet.</EmptyState>
            <button onClick={() => setNewOpen(true)} data-variant="primary" style={{ fontSize: 13, padding: "6px 12px", marginTop: 8 }}>
              <i className="ti ti-plus" style={{ fontSize: 13 }} aria-hidden="true" /> New task
            </button>
          </div>
        }
      >
        {lens === "queue"
          ? queue.map((t, i) => <TaskRow key={t.id} agentId={nodeId} task={t} rank={i} />)
          : plan.map((t) => <PlanNode key={t.id} agentId={nodeId} task={t} depth={0} />)}
      </Panel>

      <NewTaskModal open={newOpen} onClose={() => setNewOpen(false)} nodeId={nodeId} />
    </>
  );
}

/** A queue row: complete toggle + title + status + optional workflow chip. Rank #1 is the current task. */
function TaskRow({ agentId, task, rank }: { agentId: string; task: Task; rank: number }) {
  const complete = useCompleteTask();
  const moveToTop = useMoveTaskToTop();
  const done = task.status === "done";
  const isCurrent = rank === 0 && task.status === "queued";

  return (
    <PanelRow title={task.title}>
      <button
        type="button"
        onClick={() => complete.mutate({ agentId, taskId: task.id })}
        aria-label={done ? "Completed" : "Mark complete"}
        title={done ? "Completed" : "Mark complete"}
        style={completeToggle(done)}
        disabled={done}
      >
        {done && <i className="ti ti-check" style={{ fontSize: 11 }} aria-hidden="true" />}
      </button>
      {isCurrent && <span style={currentDot} title="Current task" aria-label="Current task" />}
      <span style={titleStyle(done)}>{task.title}</span>
      <TaskStatusPill status={task.status} />
      {!isCurrent && !done && (
        <button type="button" style={makeCurrentBtn} title="Make current"
          onClick={() => moveToTop.mutate(task.id, {
            onSuccess: () => showToast(`'${task.title}' is now current`),
            onError: (e) => showToast(e instanceof Error ? e.message : "Failed", "error", true),
          })}>
          Make current
        </button>
      )}
    </PanelRow>
  );
}

/** A plan-tree node: the task + its children, indented recursively. */
function PlanNode({ agentId, task, depth }: { agentId: string; task: Task; depth: number }) {
  return (
    <div>
      <div style={{ paddingLeft: depth * 18 }}>
        <PanelRow title={task.title}>
          <span style={{ ...titleStyle(task.status === "done"), flex: 1 }}>{task.title}</span>
              <TaskStatusPill status={task.status} />
        </PanelRow>
      </div>
      {(task.children ?? []).map((c) => (
        <PlanNode key={c.id} agentId={agentId} task={c} depth={depth + 1} />
      ))}
    </div>
  );
}

const segmented: CSSProperties = {
  display: "inline-flex",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  overflow: "hidden",
};
const segBtn = (on: boolean): CSSProperties => ({
  fontSize: 12,
  padding: "3px 10px",
  border: "none",
  background: on ? "var(--color-background-secondary)" : "var(--color-background-primary)",
  color: on ? "var(--color-text-primary)" : "var(--color-text-secondary)",
  fontWeight: on ? 600 : 400,
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
});
const headerBtnStyle: CSSProperties = {
  fontSize: 12,
  padding: "4px 8px",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const titleStyle = (muted: boolean): CSSProperties => ({
  fontSize: 13,
  fontWeight: 500,
  color: muted ? "var(--color-text-tertiary)" : "var(--color-text-primary)",
  textDecoration: muted ? "line-through" : "none",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
  flex: 1,
});
const completeToggle = (done: boolean): CSSProperties => ({
  width: 18,
  height: 18,
  flexShrink: 0,
  borderRadius: "50%",
  border: done ? "1px solid var(--c-teal-600)" : "1px solid var(--color-border-secondary)",
  background: done ? "var(--c-teal-600)" : "transparent",
  color: "#fff",
  cursor: done ? "default" : "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
});
const currentDot: CSSProperties = {
  width: 7,
  height: 7,
  borderRadius: "50%",
  background: "var(--c-teal-600)",
  flexShrink: 0,
};
const makeCurrentBtn: CSSProperties = {
  fontSize: 11,
  padding: "2px 8px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "transparent",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  whiteSpace: "nowrap",
};
